import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const CHANNEL_START = 0x0431
const CHANNEL_SIZE = 26
const CHANNEL_COUNT = 200
const VISIBLE_START = 0x03fd
const FILLED_START = 0x0417
const MODES = [
  'LSB',
  'USB',
  'CW',
  'CWR',
  'AM',
  'FM',
  'DIG',
  'PKT',
  'NCW',
  'NCWR',
  'NFM',
]

type FT817Spec = {
  id: string
  model: string
  summary: string
  memorySize: number
  blockLengths: readonly number[]
  usModel: boolean
  visibleStart: number
  filledStart: number
  channelStart: number
}

export type FT817Profile = {
  protocol: 'yaesu-ft817'
  id: string
  vendor: 'Yaesu'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 8
  memorySize: number
  blockLengths: readonly number[]
  usModel: boolean
  visibleStart: number
  filledStart: number
  channelStart: number
  powerLevels: readonly ['Radio']
  modes: readonly string[]
}

const BASE_BLOCKS = [2, 40, 208, 182, 208, 182, 198, 53, 130, 118]
const SPECS: readonly FT817Spec[] = [
  {
    id: 'yaesu-ft-817',
    model: 'FT-817',
    summary: 'Yaesu FT-817 HF/VHF/UHF portable.',
    memorySize: 6509,
    blockLengths: [...BASE_BLOCKS, 118],
    usModel: false,
    visibleStart: VISIBLE_START,
    filledStart: FILLED_START,
    channelStart: CHANNEL_START,
  },
  {
    id: 'yaesu-ft-817nd',
    model: 'FT-817ND',
    summary: 'Yaesu FT-817ND HF/VHF/UHF portable.',
    memorySize: 6521,
    blockLengths: [...BASE_BLOCKS, 130],
    usModel: false,
    visibleStart: VISIBLE_START,
    filledStart: FILLED_START,
    channelStart: CHANNEL_START,
  },
  {
    id: 'yaesu-ft-817nd-us',
    model: 'FT-817ND (US)',
    summary: 'Yaesu FT-817ND US version with 60-meter channels.',
    memorySize: 6651,
    blockLengths: [...BASE_BLOCKS, 130, 130],
    usModel: true,
    visibleStart: VISIBLE_START,
    filledStart: FILLED_START,
    channelStart: CHANNEL_START,
  },
  {
    id: 'yaesu-ft-818',
    model: 'FT-818',
    summary: 'Yaesu FT-818 HF/VHF/UHF portable.',
    memorySize: 6573,
    blockLengths: [2, 40, 208, 208, 208, 208, 198, 53, 130, 118, 130],
    usModel: false,
    visibleStart: 0x0431,
    filledStart: 0x044b,
    channelStart: 0x0465,
  },
  {
    id: 'yaesu-ft-818nd-us',
    model: 'FT-818ND (US)',
    summary: 'Yaesu FT-818ND US version with 60-meter channels.',
    memorySize: 6703,
    blockLengths: [2, 40, 208, 208, 208, 208, 198, 53, 130, 118, 130, 130],
    usModel: true,
    visibleStart: 0x0431,
    filledStart: 0x044b,
    channelStart: 0x0465,
  },
]

export const FT817_PROFILES: readonly FT817Profile[] = SPECS.map((spec) => ({
  protocol: 'yaesu-ft817',
  id: spec.id,
  vendor: 'Yaesu',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '200 normal channels', '8-character names'],
  baudRate: 9600,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 8,
  memorySize: spec.memorySize,
  blockLengths: spec.blockLengths,
  usModel: spec.usModel,
  visibleStart: spec.visibleStart,
  filledStart: spec.filledStart,
  channelStart: spec.channelStart,
  powerLevels: ['Radio'],
  modes: MODES,
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Radio',
    skip: false,
  }
}

function readUint32BE(raw: Uint8Array, offset: number): number {
  return (
    ((raw[offset] << 24) |
      (raw[offset + 1] << 16) |
      (raw[offset + 2] << 8) |
      raw[offset + 3]) >>>
    0
  )
}

function writeUint32BE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = (value >>> 24) & 0xff
  raw[offset + 1] = (value >>> 16) & 0xff
  raw[offset + 2] = (value >>> 8) & 0xff
  raw[offset + 3] = value & 0xff
}

function bitmap(image: Uint8Array, start: number, number: number): boolean {
  return (image[start + Math.floor((number - 1) / 8)] & (1 << ((number - 1) % 8))) !== 0
}

function setBitmap(image: Uint8Array, start: number, number: number, value: boolean): void {
  const offset = start + Math.floor((number - 1) / 8)
  const mask = 1 << ((number - 1) % 8)
  image[offset] = value ? image[offset] | mask : image[offset] & ~mask
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff || value === 0 ? 0x20 : value)),
  ).replace(/[\u0000\ufffd]/g, '').trim()
}

export function parseFT817Channel(
  image: Uint8Array,
  profile: FT817Profile,
  number: number,
): WebChannel {
  if (!bitmap(image, profile.visibleStart, number)) return emptyChannel(number)
  const start = profile.channelStart + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  const rxValue = readUint32BE(raw, 10)
  const rxFrequency = rxValue * 10
  if (!rxFrequency || rxValue === 0xffffffff) return emptyChannel(number)
  const offset = readUint32BE(raw, 14) * 10
  const duplex = raw[1] >> 6
  const hasDuplex = (raw[1] & 0x20) !== 0
  const txFrequency = !hasDuplex
    ? rxFrequency
    : duplex === 1
      ? rxFrequency - offset
      : duplex === 2
        ? rxFrequency + offset
        : offset
  const tmode = ['', 'Tone', 'TSQL', 'DTCS'][raw[4] & 0x03] ?? ''
  const tone = CTCSS_TONES[raw[6] & 0x3f] ?? null
  const dtcs = DTCS_CODES[raw[7] & 0x7f] ?? null
  let mode = MODES[raw[0] & 0x07] ?? 'FM'
  if (mode === 'FM' && raw[1] & 0x08) mode = 'NFM'
  if (mode === 'CW' && raw[1] & 0x10) mode = 'NCW'
  if (mode === 'CWR' && raw[1] & 0x10) mode = 'NCWR'
  return {
    number,
    empty: false,
    name: raw[0] & 0x80 ? decodeName(raw.slice(18, 26)) : '',
    rxFrequency,
    txFrequency,
    toneMode: tmode === 'DTCS' ? 'dtcs' : tmode ? 'ctcss' : 'none',
    ctcss: tmode === 'DTCS' ? null : tone,
    dtcs: tmode === 'DTCS' ? dtcs : null,
    dtcsPolarity: 'N',
    mode,
    power: 'Radio',
    skip: (raw[2] & 0x80) !== 0,
  }
}

export function parseFT817Channels(
  image: Uint8Array,
  profile: FT817Profile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseFT817Channel(image, profile, index + 1),
  )
}

export function applyFT817Channel(
  image: Uint8Array,
  profile: FT817Profile,
  channel: WebChannel,
): void {
  setBitmap(image, profile.visibleStart, channel.number, !channel.empty)
  setBitmap(image, profile.filledStart, channel.number, !channel.empty)
  const start = profile.channelStart + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw.every((value) => value === 0xff)) raw.fill(0)
  const tx = channel.txFrequency ?? channel.rxFrequency
  const split = Math.abs(tx - channel.rxFrequency) > 70_000_000
  const duplex = split ? 0 : tx > channel.rxFrequency ? 2 : tx < channel.rxFrequency ? 1 : 0
  const narrow = channel.mode.startsWith('N')
  const baseMode = narrow ? channel.mode.slice(1) : channel.mode
  raw[0] = (raw[0] & 0xf8) | Math.max(0, MODES.indexOf(baseMode))
  raw[0] = channel.name ? raw[0] | 0x80 : raw[0] & 0x7f
  raw[1] =
    (raw[1] & 0xe7) |
    (duplex << 6) |
    (duplex || split ? 0x20 : 0) |
    (channel.mode === 'NFM' ? 0x08 : 0) |
    (channel.mode === 'NCW' || channel.mode === 'NCWR' ? 0x10 : 0)
  raw[2] = channel.skip ? raw[2] | 0x80 : raw[2] & 0x7f
  raw[4] =
    (raw[4] & 0xfc) |
    (channel.toneMode === 'dtcs' ? 3 : channel.toneMode === 'ctcss' ? 1 : 0)
  raw[6] =
    (raw[6] & 0xc0) |
    Math.max(
      0,
      (CTCSS_TONES as readonly number[]).indexOf(
        channel.ctcss ?? CTCSS_TONES[0],
      ),
    )
  raw[7] =
    (raw[7] & 0x80) |
    Math.max(
      0,
      (DTCS_CODES as readonly number[]).indexOf(
        channel.dtcs ?? DTCS_CODES[0],
      ),
    )
  raw[8] = 0
  raw[9] = 0
  writeUint32BE(raw, 10, Math.round(channel.rxFrequency / 10))
  writeUint32BE(
    raw,
    14,
    Math.round((split ? tx : Math.abs(tx - channel.rxFrequency)) / 10),
  )
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 8)
    .padEnd(8, ' ')
  for (let index = 0; index < 8; index += 1) {
    raw[18 + index] = name.charCodeAt(index)
  }
}

function checksum(data: Uint8Array): number {
  return data.reduce((sum, value) => (sum + value) & 0xff, 0)
}

async function readBlock(
  transport: RadioTransport,
  block: number,
  length: number,
): Promise<Uint8Array> {
  const response = await transport.readExact(length + 2, 4000)
  if (
    response[0] !== block ||
    checksum(response.slice(1, -1)) !== response.at(-1)
  ) {
    throw new Error(`FT-817 block ${block} failed validation.`)
  }
  await transport.write(Uint8Array.of(ACK))
  return response.slice(1, -1)
}

export async function readFT817RadioWithTransport(
  profile: FT817Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await transport.discardInput()
  const image = new Uint8Array(profile.memorySize)
  let blockNumber = 0
  let position = 0
  for (let block = 0; block < profile.blockLengths.length; block += 1) {
    const length = profile.blockLengths[block]
    const repeat = block === 8 ? 40 : 1
    for (let count = 0; count < repeat; count += 1) {
      const data = await readBlock(transport, blockNumber, length)
      image.set(data, position)
      position += length
      blockNumber += 1
      onProgress?.({
        current: position,
        total: profile.memorySize,
        label: 'Reading Yaesu FT-817 memory',
      })
    }
  }
  return { image, reportedModel: profile.model }
}

export async function writeFT817RadioWithTransport(
  profile: FT817Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than FT-817 memory.')
  }
  let blockNumber = 0
  let position = 0
  for (let block = 0; block < profile.blockLengths.length; block += 1) {
    const length = profile.blockLengths[block]
    const repeat = block === 8 ? 40 : 1
    for (let count = 0; count < repeat; count += 1) {
      const data = image.slice(position, position + length)
      const packet = new Uint8Array(length + 2)
      packet[0] = blockNumber
      packet.set(data, 1)
      packet[packet.length - 1] = checksum(data)
      await transport.write(packet)
      if ((await transport.readExact(1, 3500))[0] !== ACK) {
        throw new Error(`FT-817 rejected block ${blockNumber}.`)
      }
      position += length
      blockNumber += 1
      onProgress?.({
        current: position,
        total: profile.memorySize,
        label: 'Writing Yaesu FT-817 memory',
      })
    }
  }
}
